import axios from '@/app/axios'
import Responses from '@/app/utils/responses'
import PaginationResponse from '@/common/pagination/entities/pagination-response'
import Evaluation from '@/evaluations/entities/evaluation'
import EvaluationFilter from '@/evaluations/entities/evaluation-filter'
import EvaluationBasicDto from '@/evaluations/models/evaluation-basic-dto'
import Guid from '@/app/utils/guid'

class EvaluationService {
  public async findOneAsync(id: string) {
    const res = await axios.get<Evaluation>(`evaluations?id=${id}`)
    return Responses.obj(res)
  }

  public async findOneByRefAsync(ref: string) {
    const res = await axios.get<Evaluation>(`evaluations/find/ref/${ref}`)
    return Responses.obj(res)
  }

  public async findAllPagedAsync(request: EvaluationFilter) {
    const res = await axios.post<PaginationResponse<Evaluation>>(
      `evaluations/find/paged`,
      request
    )
    return Responses.pagedList(res)
  }

  public async createAsync(entity: EvaluationBasicDto) {
    const res = await axios.post<EvaluationBasicDto>(`evaluations`, entity)
    return Responses.obj(res)
  }

  public async updateAsync(entity: EvaluationBasicDto) {
    const res = await axios.put<EvaluationBasicDto>(`evaluations`, entity)
    return Responses.obj(res)
  }

  public async removeAsync(entity: EvaluationBasicDto) {
    await axios.delete<EvaluationBasicDto>(`evaluations/${entity.id}/delete`)
  }

  public async updateNotesAsync(entity: EvaluationBasicDto) {
    await axios.put<EvaluationBasicDto>(`evaluations/notes`, entity)
  }

  public async startAsync(id: string) {
    await axios.post(`evaluations/${id}/start`)
  }

  public async cloneAsync(id: string) {
    const res = await axios.post<Evaluation>(`evaluations/${id}/clone`)
    return Responses.obj(res)
  }

  public async cloneWithCandidatesAsync(id: string) {
    const res = await axios.post<Evaluation>(
      `evaluations/${id}/clone-with-candidates`
    )
    return Responses.obj(res)
  }

  public async sendRating(id: string, judgeId: string) {
    const res = await axios.post(`evaluations/${id}/send/${judgeId}`)
    return res.status == 200
  }

  public async restartAsync(id: string) {
    await axios.post(`evaluations/${id}/restart`)
  }

  public async finishAsync(id: string) {
    await axios.post(`evaluations/${id}/finish`)
  }

  public async generateGuideAsync(id: string) {
    const res = await axios.get<string | null>(`evaluations/${id}/guide`)
    return Responses.obj(res)
  }

  public async generateAndSendGuideAsync(id: string) {
    await axios.get(`evaluations/${id}/guide/send`)
  }

  public async generateAndSendPreviewAsync(
    id: string,
    emailSender: string,
    emailRecipient: string,
    message: string
  ) {
    message = encodeURIComponent(message)
    await axios.get(
      `evaluations/${id}/guide/send-preview?emailSender=${emailSender}&emailRecipient=${emailRecipient}&message=${message}`
    )
  }

  public async generateObservationAsync(id: string) {
    const res = await axios.get(`evaluations/${id}/guide/observation`)
    return Responses.obj(res)
  }

  public async generateObservationWordAsync(id: string) {
    const res = await axios.get(`evaluations/${id}/guide/observation-word`)
    return Responses.obj(res)
  }

  public async generateCandidatesReportLongAsync(
    id: string,
    candidates: Guid[]
  ) {
    const res = await axios.post(
      `evaluations/${id}/guide/candidates-long`,
      candidates
    )
    return Responses.obj(res)
  }

  public async generateCandidatesReportShortAsync(
    id: string,
    candidates: Guid[]
  ) {
    const res = await axios.post(
      `evaluations/${id}/guide/candidates-short`,
      candidates
    )
    return Responses.obj(res)
  }

  public async generateDarkCandidatesReportLongAsync(
    id: string,
    candidates: Guid[]
  ) {
    const res = await axios.post(
      `evaluations/${id}/guide/candidates-long-dark`,
      candidates
    )
    return Responses.obj(res)
  }

  public async generateDarkCandidatesReportShortAsync(
    id: string,
    candidates: Guid[]
  ) {
    const res = await axios.post(
      `evaluations/${id}/guide/candidates-short-dark`,
      candidates
    )
    return Responses.obj(res)
  }

  public async generateReportAsync(id: string, candidates: Guid[]) {
    const res = await axios.post<string | null>(
      `evaluations/${id}/report`,
      candidates
    )
    return Responses.obj(res)
  }
  public async generateCompetencesReportAsync(id: string) {
    const res = await axios.post<string | null>(`evaluations/${id}/competences`)
    return Responses.obj(res)
  }

  public async downloadRadar(id: string, candidateId: string) {
    console.log('service download radar')
    const res = await axios.post<string | null>(
      `evaluations/${id}/downloadRadar/${candidateId}`
    )
    return Responses.obj(res)
  }
}

export default new EvaluationService()
